/* eslint-disable sonarjs/cognitive-complexity */
'use client';

import type { SpecialRequest } from '@ahm/api-wrappers-core';
import { FloatingLabelInput } from '@ahm/react-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Radio,
  rem,
  Stack,
  Text,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useTranslations } from 'next-intl';
import { Fragment } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useLocaleSuffix } from '@/navigation';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

interface ModalFragileItemsDeliveryProps {
  request: SpecialRequest;
  onCloseModal: () => void;
}

export default function FragileItemsDelivery({
  request,
  onCloseModal,
}: ModalFragileItemsDeliveryProps) {
  const localeSuffix = useLocaleSuffix();
  const tActions = useTranslations('common.actions');
  const isMobile = useMediaQuery('(max-width: 768px)');

  const { selectedService, setServiceRequest, serviceRequests } =
    useBookingStore((state) => state);
  const schema = z
    .object({
      option_code: z.string().nonempty('Please select an option'),
      option_detail: z.string().optional(),
    })
    .refine(
      (val) => {
        if (val.option_code === 'ITEM_6' && !val.option_detail?.trim()) {
          return false;
        }
        return true;
      },
      {
        message: 'Please provide details for "Other" option',
        path: ['option_detail'],
      }
    );

  type FormData = z.infer<typeof schema>;

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      option_code: '',
      option_detail: '',
    },
  });

  const watchOptionCode = watch('option_code');

  const onSubmit = (data: FormData) => {
    if (!selectedService?._id) return;

    const param = { ...serviceRequests };
    param[request._id as keyof typeof serviceRequests] = {
      option_code: data.option_code,
      ...(data?.option_detail && { option_detail: data.option_detail }),
    };
    setServiceRequest(param);
    onCloseModal();
  };

  return (
    <Box bg={'white'} py={rem(16)}>
      <Text px={isMobile ? 16 : 24} c={'neutral.7'} size="sm">
        {request[`description_${localeSuffix}`]}
      </Text>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="option_code"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Radio.Group
              mb={{ base: rem(36), sm: 'md' }}
              bg="white"
              value={value}
              onChange={(value) => onChange(value)}
            >
              <Stack gap={0}>
                {request?.options?.map((item, idx) => {
                  return (
                    <Fragment key={item.code}>
                      {idx > 0 ? <Divider mx="md" /> : null}
                      <Button
                        variant="subtle"
                        size="md"
                        py={16}
                        px={isMobile ? 16 : 24}
                        h="auto"
                        color="neutral"
                        fw={500}
                        fullWidth
                        radius={0}
                        justify="space-between"
                        component="label"
                        rightSection={
                          <Radio
                            value={item.code}
                            id={`item-type-${item.code}`}
                            checked={value === item.code}
                          />
                        }
                        styles={{
                          root: {},
                        }}
                      >
                        <Text
                          style={{ whiteSpace: 'normal' }}
                          ta={'left'}
                          lineClamp={2}
                          truncate
                          size="md"
                          c={'neutral.7'}
                        >
                          {item[`name_${localeSuffix}`]}
                        </Text>
                      </Button>
                    </Fragment>
                  );
                })}
                <Collapse in={watchOptionCode === 'ITEM_6'}>
                  <Box pt="sm" px={isMobile ? 16 : 24} pb="md">
                    <Controller
                      control={control}
                      name="option_detail"
                      render={({ field: { onChange, value } }) => (
                        <FloatingLabelInput
                          error={errors.option_detail?.message}
                          label={'Ghi chú cho tài xế'}
                          value={value}
                          onChange={(value) => onChange(value)}
                        />
                      )}
                    />
                  </Box>
                </Collapse>
              </Stack>
            </Radio.Group>
          )}
        />

        <Button
          fullWidth
          m={24}
          mb={isMobile ? 0 : 8}
          w={'calc(100% - 48px)'}
          size="md"
          mih={rem(48)}
          type="submit"
          disabled={!isValid}
        >
          {tActions('confirm')}
        </Button>
      </form>
    </Box>
  );
}
